/**
 * @file The one-tag summary of what pricing a Signify response carries. Consumers: the single-part
 * product modal and the multi-part fetch job's Price cell, which shows it when a fetched part has
 * no usable price to print.
 * The prop is the narrowest readonly shape the tag reads rather than the full response type,
 * because the fetch job's cell holds a valtio snapshot and `Snapshot<T>` freezes `prices` into a
 * `readonly` array that the response type will not accept. Both callers pass more than this.
 * A response with no `price` at all renders nothing: the caller is still fetching, and a tag saying
 * so would be indistinguishable from the vendor refusing a price.
 */

import { FC } from 'react';
import { Tag } from 'antd';

type PricingStatusSource = {
  priceError?: string;
  price?: { prices?: readonly { priceTypeIdentifier: string }[] } | null;
};

interface PricingStatusTagProps {
  productPriceAndAvailabilityData: PricingStatusSource | null;
}

const PricingStatusTag: FC<PricingStatusTagProps> = ({ productPriceAndAvailabilityData }) => {
  if (!productPriceAndAvailabilityData) return null;

  if (productPriceAndAvailabilityData.priceError) {
    return <Tag color="error">Price Not Available</Tag>;
  }

  const prices = productPriceAndAvailabilityData.price?.prices;
  if (prices) {
    const hasPricing = prices.some((price) => price.priceTypeIdentifier.includes('PRICE'));
    const hasCommission = prices.some((price) => price.priceTypeIdentifier.includes('COMMISSION'));

    if (hasPricing) {
      return <Tag color="success">Pricing Available</Tag>;
    }

    if (hasCommission) {
      return <Tag color="warning">Commission Only</Tag>;
    }

    return <Tag color="error">Missing Price Data</Tag>;
  }

  return null;
};

export default PricingStatusTag;
